import React from 'react';
import { X, Phone, Mail, MapPin, Calendar, DollarSign, Music, FileText, Tag } from 'lucide-react';
import { AVAILABLE_TAGS, getTagColor, getTagLabel } from '../../utils/contactTags';

const ContactModal = ({ contact, isOpen, onClose }) => {
  if (!isOpen || !contact) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
          <h2 className="text-2xl font-bold mb-1">{contact.contactName || 'Contact Details'}</h2>
          {contact.venue && <p className="text-purple-100">{contact.venue}</p>}
          
          {/* Contact Tags in Header */}
          {contact.contactTags && contact.contactTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {contact.contactTags.map(tag => (
                <span 
                  key={tag} 
                  className="px-3 py-1 rounded-full text-sm font-semibold bg-white/20 text-white"
                >
                  {getTagLabel(tag)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
          <div className="space-y-6">
            
            {/* Basic Contact Info */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                Contact Information
              </h3>
              <div className="space-y-3">
                {contact.contactEmail && (
                  <a
                    href={`mailto:${contact.contactEmail}`}
                    className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Mail className="text-purple-600 dark:text-purple-400" size={20} />
                    <span className="text-gray-800 dark:text-gray-100">{contact.contactEmail}</span>
                  </a>
                )}
                {contact.contactPhone && (
                  <a
                    href={`tel:${contact.contactPhone}`}
                    className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Phone className="text-purple-600 dark:text-purple-400" size={20} />
                    <span className="text-gray-800 dark:text-gray-100">{contact.contactPhone}</span>
                  </a>
                )}
              </div>
            </div>

            {/* Contact Tags Section */}
            {contact.contactTags && contact.contactTags.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Tag size={16} />
                  Contact Type
                </h3>
                <div className="flex flex-wrap gap-2">
                  {contact.contactTags.map(tag => (
                    <span 
                      key={tag} 
                      className={`px-4 py-2 rounded-full text-sm font-semibold ${getTagColor(tag)}`}
                    >
                      {getTagLabel(tag)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Show Details */}
            {(contact.title || contact.city) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                  Show Details
                </h3>
                <div className="space-y-3">
                  {contact.title && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Music className="text-purple-600 dark:text-purple-400" size={20} />
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Show Title</p>
                        <p className="text-gray-800 dark:text-gray-100 font-medium">{contact.title}</p>
                      </div>
                    </div>
                  )}
                  {contact.city && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <MapPin className="text-purple-600 dark:text-purple-400" size={20} />
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">City</p>
                        <p className="text-gray-800 dark:text-gray-100 font-medium">{contact.city}</p>
                      </div>
                    </div>
                  )}
                  {(contact.showTime || contact.loadInTime) && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <Calendar className="text-purple-600 dark:text-purple-400" size={20} />
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Times</p>
                        <div className="text-gray-800 dark:text-gray-100 font-medium">
                          {contact.loadInTime && <span>Load In: {contact.loadInTime}</span>}
                          {contact.loadInTime && contact.showTime && <span className="mx-2">•</span>}
                          {contact.showTime && <span>Show: {contact.showTime}</span>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Financial Info */}
            {(contact.guarantee || contact.cashMerch || contact.venmoMerch) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                  Financial Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {contact.guarantee && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-xs text-green-600 dark:text-green-400 mb-1">Guarantee</p>
                      <p className="text-lg font-bold text-green-700 dark:text-green-300">${contact.guarantee}</p>
                    </div>
                  )}
                  {contact.cashMerch && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Cash Merch</p>
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300">${contact.cashMerch}</p>
                    </div>
                  )}
                  {contact.venmoMerch && (
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">Venmo Merch</p>
                      <p className="text-lg font-bold text-purple-700 dark:text-purple-300">${contact.venmoMerch}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Expenses */}
            {(contact.gas || contact.hotel || contact.food || contact.tolls || contact.otherExpenses) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                  Expenses
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {contact.gas && (
                    <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Gas</p>
                      <p className="font-semibold text-gray-800 dark:text-gray-100">${contact.gas}</p>
                    </div>
                  )}
                  {contact.hotel && (
                    <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Hotel</p>
                      <p className="font-semibold text-gray-800 dark:text-gray-100">${contact.hotel}</p>
                    </div>
                  )}
                  {contact.food && (
                    <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Food</p>
                      <p className="font-semibold text-gray-800 dark:text-gray-100">${contact.food}</p>
                    </div>
                  )}
                  {contact.tolls && (
                    <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Tolls</p>
                      <p className="font-semibold text-gray-800 dark:text-gray-100">${contact.tolls}</p>
                    </div>
                  )}
                  {contact.otherExpenses && (
                    <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Other</p>
                      <p className="font-semibold text-gray-800 dark:text-gray-100">${contact.otherExpenses}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            {(contact.showNotes || contact.setlist || contact.crowdInfo || contact.technicalNotes || contact.nextTime) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                  Notes
                </h3>
                <div className="space-y-3">
                  {contact.showNotes && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-2">
                        <FileText size={14} /> Show Notes
                      </p>
                      <p className="text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{contact.showNotes}</p>
                    </div>
                  )}
                  {contact.setlist && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-2">
                        <Music size={14} /> Setlist
                      </p>
                      <p className="text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{contact.setlist}</p>
                    </div>
                  )}
                  {contact.crowdInfo && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Crowd Info</p>
                      <p className="text-gray-800 dark:text-gray-100">{contact.crowdInfo}</p>
                    </div>
                  )}
                  {contact.technicalNotes && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Technical Notes</p>
                      <p className="text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{contact.technicalNotes}</p>
                    </div>
                  )}
                  {contact.nextTime && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">For Next Time</p>
                      <p className="text-blue-800 dark:text-blue-200">{contact.nextTime}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
          <button
            onClick={onClose}
            className="w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;